import React, { useMemo } from 'react';
import {
  View,
  Pressable,
  StyleSheet,
  StyleProp,
  ViewStyle,
  TextStyle,
} from 'react-native';
import { Text } from '@/components/common/text';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useHoverStyle } from '@/hooks/shared/useHoverStyle';

export interface GlassSidebarNavItemProps {
  title: string;
  icon?: React.ReactNode;
  isActive: boolean;
  onPress: () => void;
  description?: string;
  iconOnly?: boolean;
  style?: StyleProp<ViewStyle>;
  titleStyle?: StyleProp<TextStyle>;
}

export const GlassSidebarNavItem: React.FC<GlassSidebarNavItemProps> = React.memo(
  ({
    title,
    icon,
    isActive,
    onPress,
    description,
    iconOnly = false,
    style,
    titleStyle,
  }) => {
    const styles = useStyles();
    // The sidebar keeps its own itemHoverBg tint; the hook adds the rest cursor
    // and transition the other hover surfaces share.
    const { isHovered, hoverProps, viewStyle } = useHoverStyle('none');

    const containerStyle = useMemo(
      () => [
        styles.container,
        iconOnly && styles.containerIconOnly,
        isActive && styles.containerActive,
        !isActive && isHovered && styles.containerHover,
        style,
        viewStyle,
      ],
      [styles, iconOnly, isActive, isHovered, style, viewStyle],
    );

    return (
      <Pressable
        onPress={onPress}
        style={containerStyle}
        accessibilityRole="button"
        accessibilityLabel={title}
        accessibilityState={{ selected: isActive }}
        {...hoverProps}
      >
        {isActive ? <View style={styles.activeIndicator} /> : null}
        {icon ? <View style={styles.iconSlot}>{icon}</View> : null}
        {!iconOnly ? (
          <View style={styles.textSlot}>
            <Text
              style={[styles.title, isActive && styles.titleActive, titleStyle]}
              numberOfLines={2}
            >
              {title}
            </Text>
            {description ? (
              <Text style={styles.description} numberOfLines={1}>
                {description}
              </Text>
            ) : null}
          </View>
        ) : null}
      </Pressable>
    );
  },
);

GlassSidebarNavItem.displayName = 'GlassSidebarNavItem';

const useStyles = () =>
  useThemedStyles(({ components }) =>
    StyleSheet.create({
      container: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        paddingHorizontal: 14,
        paddingVertical: 10,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: 'transparent',
        position: 'relative',
        overflow: 'hidden',
      },
      containerIconOnly: {
        justifyContent: 'center',
        paddingHorizontal: 8,
      },
      containerActive: {
        backgroundColor: components.admin.sidebar.itemActiveBg,
      },
      containerHover: {
        backgroundColor: components.admin.sidebar.itemHoverBg,
      },
      activeIndicator: {
        position: 'absolute',
        left: 0,
        top: 8,
        bottom: 8,
        width: 3,
        borderTopRightRadius: 3,
        borderBottomRightRadius: 3,
        backgroundColor: components.admin.sidebar.indicator,
      },
      iconSlot: {
        width: 22,
        height: 22,
        alignItems: 'center',
        justifyContent: 'center',
      },
      textSlot: {
        flex: 1,
        gap: 2,
      },
      title: {
        fontSize: 14,
        fontWeight: '500',
        color: components.admin.sidebar.label,
      },
      titleActive: {
        fontWeight: '600',
      },
      description: {
        fontSize: 12,
        fontWeight: '400',
        color: components.admin.sidebar.description,
      },
    }),
  );
